import { Button } from '@hologram-dimension/button';
import { Icon } from '@hologram-dimension/icon';
import { clsx } from 'clsx/lite';
import React, { MouseEventHandler, useCallback, useState } from 'react';
import type { useForm } from 'react-hook-form';
import { SimInventorySegmentName } from 'types/common';
import filterStyles from '../Filters/filterStyles.module.css';
import { InputFilters } from '../shared';
import styles from './FilterAccordion.module.css';

export interface FilterAccordionProps {
  className?: string;
  children?: React.ReactNode;
  onClear?: () => void;
  label: string;
  isSet: boolean;
  defaultActive?: boolean;
}

export interface FilterProps {
  submit: AnyFunction;
  onClear: () => void;
  segmentName: SimInventorySegmentName;
  form: ReturnType<typeof useForm<InputFilters>>;
}

const FilterAccordion: React.FC<FilterAccordionProps> = ({
  className,
  children,
  onClear,
  label,
  isSet,
  defaultActive = false,
}) => {
  const [isActive, setIsActive] = useState(defaultActive);
  const handleClick = useCallback(() => {
    setIsActive((prevActive) => !prevActive);
  }, []);

  const handleKey: React.KeyboardEventHandler<HTMLDivElement> = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setIsActive((prevActive) => !prevActive);
    }
  }, []);

  const onClearClick = useCallback<
    MouseEventHandler & MouseEventHandler<HTMLAnchorElement> & MouseEventHandler<HTMLButtonElement>
  >(
    (e) => {
      e.stopPropagation();
      if (onClear) onClear();
    },
    [onClear]
  );

  return (
    <div className={clsx(className)}>
      <div className={styles.filter}>
        <div
          className={clsx(styles.filterHeader, isActive && styles.expanded)}
          onClick={handleClick}
          onKeyDown={handleKey}
          tabIndex={0}
          role="button"
          aria-label={`${label} filter`}
          data-testid="filterAccordionControl"
        >
          <div className={styles.filterHeaderLeftContent}>
            <Icon
              className={styles.filterIcon}
              name={isActive ? 'ChevronSingleSouth' : 'ChevronSingleEast'}
              fill="DdsColorPaletteBlack"
            />
            <div className={styles.filterName}>{label}</div>
          </div>
          {isSet && (
            <Button
              className={styles.clearButton}
              variant="tertiary"
              aria-label={`Filter - ${label} clear`}
              onClick={onClearClick}
            >
              Clear
            </Button>
          )}
        </div>
        {isActive && (
          <div className={styles.filterContentWrapper}>
            <hr className={filterStyles.divider} />
            <div className={styles.filterContent}>{children}</div>
            {children !== undefined && <hr className={filterStyles.divider} />}
          </div>
        )}
      </div>
    </div>
  );
};

export default FilterAccordion;
